import { AddCircleOutline, BlockOutlined } from '@mui/icons-material'
import { Box, styled, Tooltip, Typography, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MainButton } from 'src/components/MainButton'
import { IReportTemplate } from 'src/generated-types/report-template'
import { ReportTemplateStatus } from 'src/enums'
import { MUIThemeColors } from 'src/mui-theme'
import { GetRGBA, getThemeColor } from 'src/utils'

export interface FormSchemaAccordionHeaderProps {
  formSchemaId: number
  formSchemaName: string
  reportTemplates: IReportTemplate[]
  onCreateTemplate: (formSchemaId?: number) => void
  isFormSchemaDeleted?: boolean
  isFormSchemaInactive?: boolean
}

const STATUS_ORDER: ReportTemplateStatus[] = [
  ReportTemplateStatus.Active,
  ReportTemplateStatus.Inactive,
  ReportTemplateStatus.Draft,
  ReportTemplateStatus.Warning,
]

export const getStatusConfig = (
  theme: any,
): Record<ReportTemplateStatus, { labelKey: string; bgColor: string; color: string }> => {
  const activeColor = getThemeColor(theme, MUIThemeColors.successLight)
  const inactiveColor = getThemeColor(theme, MUIThemeColors.secondaryMain)
  const draftColor = getThemeColor(theme, MUIThemeColors.infoMain)
  const warningColor = getThemeColor(theme, MUIThemeColors.warningLight)

  return {
    [ReportTemplateStatus.Active]: {
      labelKey: 'active',
      bgColor: GetRGBA(activeColor, 0.15),
      color: activeColor,
    },
    [ReportTemplateStatus.Inactive]: {
      labelKey: 'inactive',
      bgColor: GetRGBA(inactiveColor, 0.15),
      color: inactiveColor,
    },
    [ReportTemplateStatus.Draft]: {
      labelKey: 'draft',
      bgColor: GetRGBA(draftColor, 0.15),
      color: draftColor,
    },
    [ReportTemplateStatus.Warning]: {
      labelKey: 'warning',
      bgColor: GetRGBA(warningColor, 0.15),
      color: warningColor,
    },
  }
}

export const FormSchemaAccordionHeader: React.FC<FormSchemaAccordionHeaderProps> = ({
  formSchemaName,
  reportTemplates,
  onCreateTemplate,
  formSchemaId,
  isFormSchemaDeleted = false,
  isFormSchemaInactive = false,
}) => {
  const theme = useTheme()
  const { t: translate } = useTranslation('dynamic-report')
  const STATUS_CONFIG = useMemo(() => getStatusConfig(theme), [theme])

  const statusCounts = useMemo(() => {
    const counts: Partial<Record<ReportTemplateStatus, number>> = {}
    for (const tpl of reportTemplates) {
      counts[tpl.status] = (counts[tpl.status] ?? 0) + 1
    }
    return counts
  }, [reportTemplates])

  const statusEntries = useMemo(() => STATUS_ORDER.filter(status => statusCounts[status] !== undefined), [statusCounts])

  const isDisabled = isFormSchemaDeleted || isFormSchemaInactive
  const disabledTooltipKey = isFormSchemaDeleted
    ? 'create-template-form-schema-deleted'
    : 'create-template-form-schema-inactive'

  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={1}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="body1" fontWeight={600} color="text.primary">
          {translate('form-schema')}: {formSchemaName}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        {statusEntries.length > 0 && (
          <Box display="flex" alignItems="center" gap={0.75}>
            {statusEntries.map(status => {
              const cfg = STATUS_CONFIG[status]
              return (
                <Tooltip
                  key={status}
                  title={translate(cfg.labelKey, { ns: 'dynamic-report' }).toUpperCase() as string}
                  arrow
                >
                  <StatusCountBadge badgeBg={cfg.bgColor} badgeColor={cfg.color}>
                    {statusCounts[status]}
                  </StatusCountBadge>
                </Tooltip>
              )
            })}
            <Tooltip title={translate('total-templates', { ns: 'dynamic-report' }) as string} arrow>
              <StatusCountBadge
                badgeBg={GetRGBA(getThemeColor(theme, MUIThemeColors.primaryLight), 0.15)}
                badgeColor={getThemeColor(theme, MUIThemeColors.primaryLight)}
              >
                {translate('total', { ns: 'dynamic-report' })}: {reportTemplates.length}
              </StatusCountBadge>
            </Tooltip>
          </Box>
        )}
        <Tooltip title={isDisabled ? (translate(disabledTooltipKey, { ns: 'dynamic-report' }) as string) : ''}>
          <span>
            <MainButton
              size="small"
              variant="outlined"
              onClick={e => {
                e.stopPropagation()
                onCreateTemplate(formSchemaId)
              }}
              sx={{ fontSize: '12px', minWidth: 'auto', minHeight: 'auto', padding: '4px 12px' }}
              startIcon={isDisabled ? <BlockOutlined fontSize="small" /> : <AddCircleOutline />}
              disabled={isDisabled}
            >
              {translate('create-template')}
            </MainButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  )
}

const StatusCountBadge = styled(Box, {
  shouldForwardProp: prop => prop !== 'badgeBg' && prop !== 'badgeColor',
})<{ badgeBg: string; badgeColor: string }>(({ badgeBg, badgeColor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  padding: '3px 12px',
  borderRadius: '6px',
  backgroundColor: badgeBg,
  color: badgeColor,
  fontSize: '14px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  cursor: 'default',
}))
